import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { createTestimonial } from "@/lib/api/marketing.functions";
import { AdminFormFooter } from "@/components/admin/AdminFormFooter";
import { AdminPageHeader } from "@/components/admin/AdminPageHeader";
import { AdminPageShell } from "@/components/admin/AdminPageShell";
import { MarketingAdminNav } from "@/components/admin/MarketingAdminNav";
import { MediaField } from "@/components/admin/MediaPicker";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import type { MediaSelection } from "@/lib/media/types";

export const Route = createFileRoute("/admin/marketing/testimonials/new")({
  component: NewTestimonialPage,
});

function NewTestimonialPage() {
  const navigate = useNavigate();
  const [quote, setQuote] = useState("");
  const [name, setName] = useState("");
  const [role, setRole] = useState("");
  const [image, setImage] = useState<MediaSelection>({});
  const [status, setStatus] = useState<"draft" | "published">("published");
  const [saving, setSaving] = useState(false);

  async function handleSave() {
    setSaving(true);
    try {
      const row = await createTestimonial({
        data: {
          quote,
          name,
          role,
          imageMediaId: image.mediaId,
          imageUrl: image.url,
          status,
        },
      });
      toast.success("Testimonial created");
      navigate({ to: "/admin/marketing/testimonials/$id", params: { id: row.id } });
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to create");
    } finally {
      setSaving(false);
    }
  }

  return (
    <AdminPageShell>
      <AdminPageHeader title="Add testimonial" />
      <MarketingAdminNav />
      <div className="space-y-4">
        <div className="space-y-2">
          <Label>Quote</Label>
          <Textarea value={quote} onChange={(e) => setQuote(e.target.value)} rows={4} />
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-2">
            <Label>Name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <div className="space-y-2">
            <Label>Role</Label>
            <Input value={role} onChange={(e) => setRole(e.target.value)} placeholder="Audit Senior" />
          </div>
        </div>
        <MediaField label="Portrait" value={image} onChange={setImage} />
        <div className="space-y-2">
          <Label>Status</Label>
          <Select value={status} onValueChange={(v) => setStatus(v as typeof status)}>
            <SelectTrigger className="max-w-xs"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="draft">Draft</SelectItem>
              <SelectItem value="published">Published</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      <AdminFormFooter
        cancelTo="/admin/marketing/testimonials"
        onSave={handleSave}
        saving={saving}
        saveLabel="Create testimonial"
        savingLabel="Creating…"
      />
    </AdminPageShell>
  );
}
